import { InformationItem, TileInformationDataType } from "@/api/dtos";
import Icon from "@/components/layouts/Icon/Icon";
import { ITrial } from "@/components/TrialsArea";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import useInformationData from "@/hooks/tiles/useInformationData";
import { faChevronDown, faChevronUp, faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { Box, ButtonBase, CircularProgress, darken, Divider, lighten, ListItemText, styled } from "@mui/material";
import { ComponentType, FC, Fragment, useContext, useMemo } from "react";
import { ITileInformationDataComponentProps } from ".";
import { IconError, StyledList, StyledListItem, StyledListItemButton, StyledListItemIcon } from "..";
import TileInformationDataListComponent from "./Components/TileInformationDataListComponent";



const AppearingButton = styled(ButtonBase)(({ theme }) => ({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    borderRadius: '50%',
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5),

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    opacity: 0,
    transition: 'opacity 0.5s linear',
}));

const ExpandButtonBox = styled(Box)(() => ({
    width: '22.5px',
    height: '22.5px',
    flex: '0 0 auto',
    alignSelf: 'center',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    userSelect: 'none',
}));

const TileInformationDataMapping: Partial<Record<TileInformationDataType, ComponentType<ITileInformationDataComponentProps>>> = {
    [TileInformationDataType.List]: TileInformationDataListComponent
}

interface ITileInformationDataProps {
    informationItem: InformationItem;
    trial: ITrial;
    expandedMenuItemsNonFav?: Record<number, boolean> | undefined;
    toggleExpandedMenuItem?: (trialId: number, expandMenuItemId: number) => void | undefined;
    toggleExpandedMenuItemNonfav?: (expandMenuItemId: number) => void | undefined;
}

const TileInformationData: FC<ITileInformationDataProps> = ({ 
    trial, 
    informationItem,
    expandedMenuItemsNonFav,
    toggleExpandedMenuItemNonfav,
    toggleExpandedMenuItem
}) => {

    const institution = useContext(InstitutionContext);

    const apiLinks = useMemo(() => {
        if (!informationItem.apiLink) {
            return undefined;
        }

        return [informationItem.apiLink];
    }, [informationItem.apiLink]);

    const expanded = ((informationItem?.expandStateMenuItemId && 
        trial?.expandedMenuItemStates && 
        trial?.expandedMenuItemStates[informationItem?.expandStateMenuItemId] === true) || 
        (expandedMenuItemsNonFav &&
        informationItem.expandStateMenuItemId &&
        expandedMenuItemsNonFav[informationItem.expandStateMenuItemId] === true))
        
    // load the hook for the data getting pulled by passing API link

    const { data, loading } = useInformationData(expanded ? trial.id : undefined, institution.data?.id?.toString() , apiLinks);

    const itemData = data && informationItem.dataKey ? data[informationItem.dataKey] : undefined;

    const Component = itemData?.type ? TileInformationDataMapping[itemData.type] : undefined;

    const showInformation = ((!loading && data) || !apiLinks) && !!Component;

    return (
        <>
            <StyledListItemButton
                onClick={() => {(toggleExpandedMenuItem && informationItem.expandStateMenuItemId) ? 
                        toggleExpandedMenuItem(trial.id, informationItem.expandStateMenuItemId) 
                    : 
                    toggleExpandedMenuItemNonfav && informationItem.expandStateMenuItemId &&
                    toggleExpandedMenuItemNonfav(informationItem.expandStateMenuItemId)}}
            >
                <StyledListItemIcon>
                    <Icon icon={{ prefix: informationItem.iconPrefix as any, iconName: informationItem.iconName as any }} size="lg" fixedWidth />
                </StyledListItemIcon>
                <ListItemText 
                    primary={informationItem.name}
                />
                {
                    loading ?
                        <CircularProgress size={16} /> :
                        <ExpandButtonBox>
                            <AppearingButton
                                // onClick={toggle} // This is not needed as the parent has the onClick event
                                className="expandIcon"
                            >
                                <Icon fixedWidth icon={expanded ? faChevronUp : faChevronDown } size='2xs'/>
                            </AppearingButton>
                            <Icon fixedWidth icon={expanded ? faChevronUp : faChevronDown } size='lg'/>
                        </ExpandButtonBox>
                }
            </StyledListItemButton>
            {
                showInformation && expanded && (
                    <>
                        {
                            itemData ? (
                                <>
                                    <Divider />
                                    <Component data={itemData} />
                                </>

                            ) : (
                                <>
                                    <Divider />
                                    <StyledList
                                        disablePadding
                                    >
                                        <Fragment key={"Error " + informationItem.apiLink}>
                                            <StyledListItem>
                                                <StyledListItemIcon>
                                                    <IconError icon={faExclamationCircle} size="lg" fixedWidth />
                                                </StyledListItemIcon>
                                                <ListItemText 
                                                    primary={"Error"}
                                                />
                                            </StyledListItem>
                                            
                                        </Fragment>
                                        
                                    </StyledList>
                                    
                                </>

                            )
                        }
                    </>
                )
            
            }
            {
                expanded && !itemData && !loading && (
                    <>
                        <Divider />
                        <StyledList
                            disablePadding
                        >
                            <Fragment key={"Error " + informationItem.apiLink}>
                                <StyledListItem>
                                    <StyledListItemIcon>
                                        <IconError icon={faExclamationCircle} size="lg" fixedWidth />
                                    </StyledListItemIcon>
                                    <ListItemText 
                                        primary={"Error"}
                                    />
                                </StyledListItem>
                                
                            </Fragment>
                            
                        </StyledList>
                        
                    </>
                )
            }
        </>
    )
}

export default TileInformationData;